import { graphql } from "src/api/graphql/gql"
import { List } from "antd"
import { useQuery } from "@apollo/client"
import { ManageDatasourceListItem } from "src/pages/DataAssets/drawers/ManageDataSources/ManageDatasourceListItem.tsx"
import { DeleteDatasourceModal } from "src/pages/DataAssets/components/DeleteDatasourceModal.tsx"
import { useEffect, useState } from "react"
import { EditDataSourceDrawer } from "src/pages/DataAssets/drawers/ManageDataSources/EditDataSourceDrawer.tsx"
import { NativeCloudSupportedDataSource } from "src/pages/DataAssets/schemas/data-source-schemas.ts"
import { Drawer } from "src/global/ui/Drawer/Drawer.tsx"
import { sortBy } from "lodash-es"
import { useEventAction } from "src/global/hooks/useEventAction"
import { EventTypes } from "src/types/events"
import { useRealTimeUpdateMessage } from "src/global/hooks/useRealTimeUpdateMessage"

const REAL_TIME_BUFFER_DELAY = 1000
export const REAL_TIME_UPDATE_MESSAGE = "Data source updated"

export const DataSourcesListDrawerDocument = graphql(`
  query DataSourcesListDrawer {
    datasourcesV2 {
      id
      name
      type
      assets {
        id
      }
    }
  }
`)

type Props = {
  open: boolean
  close: () => void
}

type DataSourceToEdit = { name: string; id: string; type: NativeCloudSupportedDataSource } | undefined

export function ManageDatasourcesDrawer({ open, close }: Props) {
  const { data, loading, refetch } = useQuery(DataSourcesListDrawerDocument)
  const sortedDataSources = data?.datasourcesV2 && sortBy(data.datasourcesV2, (ds) => ds.name.toLowerCase())
  const [dataSourceToDelete, setDataSourceToDelete] = useState<
    { name: string; id: string; assetCount: number } | undefined
  >()
  const [dataSourceToEdit, setDataSourceToEdit] = useState<DataSourceToEdit>()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const { show: showDataSourceChangedMessage, container: dataSourceChangedMessage } = useRealTimeUpdateMessage({
    messageContent: REAL_TIME_UPDATE_MESSAGE,
    onRefresh: () => {
      refetch()
    },
  })

  /*
   * Handle real-time event updates
   */
  useEventAction(EventTypes.DATA_SOURCE.CREATED, () => showDataSourceChangedMessage(), {
    bufferDelay: REAL_TIME_BUFFER_DELAY,
  })
  useEventAction(EventTypes.DATA_SOURCE.UPDATED, () => showDataSourceChangedMessage(), {
    bufferDelay: REAL_TIME_BUFFER_DELAY,
  })
  useEventAction(EventTypes.DATA_SOURCE.DELETED, () => showDataSourceChangedMessage(), {
    bufferDelay: REAL_TIME_BUFFER_DELAY,
  })

  useEffect(() => {
    if (dataSourceToEdit) {
      setIsDrawerOpen(true)
    }
  }, [dataSourceToEdit])

  return (
    <Drawer title="Manage Data Sources" open={open} onClose={close} size="large">
      {dataSourceChangedMessage}
      {dataSourceToDelete && (
        <DeleteDatasourceModal
          datasource={dataSourceToDelete}
          isVisible={!!dataSourceToDelete}
          setIsVisible={() => setDataSourceToDelete(undefined)}
        />
      )}
      <EditDataSourceDrawer
        isOpen={isDrawerOpen}
        afterClose={() => setDataSourceToEdit(undefined)}
        onClose={() => setIsDrawerOpen(false)}
        dataSource={dataSourceToEdit}
      />
      <List
        dataSource={sortedDataSources ?? []}
        loading={loading}
        rowKey={(datasource) => datasource.id}
        renderItem={(dataSource) => (
          <ManageDatasourceListItem
            dataSource={dataSource}
            onDelete={() =>
              setDataSourceToDelete({ name: dataSource.name, id: dataSource.id, assetCount: dataSource.assets.length })
            }
            onEdit={() => setDataSourceToEdit(dataSource as DataSourceToEdit)} // don't love casting, but the button that can call this is disabled if type is not supported
          />
        )}
      />
    </Drawer>
  )
}
